import React, { useEffect, useState, useContext, useCallback } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import moment from "moment";

import DispatchContext from "../../contexts/DispatchContext";
import DataFetcher from "../../utils/DataFetcher";
import { dispError, dispSuccess } from "../../utils/Tools";
import ErrorLine from "../elements/ErrorLine";
import LoadingIndicator from "../elements/LoadingIndicator";

import { sortBy, compareValues } from "../../utils/Tools";

function FormLicencesAssign(props) {
  const appDispatch = useContext(DispatchContext);
  const [selection, setSelection] = useState(null);
  const [maxLicences, setMaxLicences] = useState(null);

  const prefix = props.usertype === "super-admin" ? "client" : "institution";

  const formValues = {
    client: {
      selectionURL: "/client/list",
      selectionPayload: { small: "__ll_true__" },
      selectionType: "client_id",
      selectionLabel: "Mandant",
      selectionError: "Bitte wählen Sie einen Mandanten aus.",
      saveURL: "/client/assign-licences"
    },
    institution: {
      selectionURL: "/client/institution-licencing-list",
      selectionPayload: {},
      selectionType: "institution_id",
      selectionLabel: "Institution",
      selectionError: "Bitte wählen Sie eine Institution aus.",
      saveURL: "/institution/assign-licences"
    }
  };

  const loadData = useCallback(() => {
    let targetURL = formValues[prefix].selectionURL;
    let payload = formValues[prefix].selectionPayload;
    DataFetcher.fetch(targetURL, payload)
      .then(response => {
        if (!response.success) {
          return appDispatch(dispError(response.msg));
        }
        if (prefix === "institution") {
          setMaxLicences(response.licences.available);
        }
        setSelection(response.list);
      })
      .catch(error => {
        console.error(error);
      });
  }, [appDispatch, formValues, prefix]);

  useEffect(() => {
    if (!selection) {
      loadData();
    }
  }, [selection, loadData]);

  const yupObject = {};
  yupObject[formValues[prefix].selectionType] = Yup.string().required(formValues[prefix].selectionError);
  if (prefix !== "client") yupObject.valid_until = Yup.date().min(moment().add(1, "D"), "Bitte geben Sie ein gültiges Datum ein.").required("Bitte geben Sie ein Datum ein.");
  if (maxLicences) {
    yupObject.licencecount = Yup.number().min(1, "Bitte geben Sie die gewünschte Lizenzmenge an.").max(maxLicences, `Es stehen nur ${maxLicences} Lizenzen zur Verfügung.`).required("Bitte geben Sie die gewünschte Lizenzmenge an.");
  } else {
    yupObject.licencecount = Yup.number().min(1, "Bitte geben Sie die gewünschte Lizenzmenge an.").required("Bitte geben Sie die gewünschte Lizenzmenge an.");
  }

  const defaultValues = {};
  defaultValues[formValues[prefix].selectionType] = "";
  if (prefix !== "client") defaultValues.valid_until = moment().add(3, "M").format("YYYY-MM-DD");
  defaultValues.licencecount = 0;

  const targetURL = formValues[prefix].saveURL;

  const getSelectionName = id => selection.filter(entry => entry._id === id)[0].name;
  const getAssignedLicences = resData => (typeof resData === "object" ? resData.licencecount : resData);
  const getDropdownOptions = (array, fieldName, defVal = "Wählen") => {
    let items = [];
    if (prefix === "institution") {
      items = array
        .filter(item => item.interestGroup !== "self")
        .sort(compareValues("name"))
        .concat(array.filter(item => item.interestGroup === "self"))
        .map((item, index) => {
          let label = item.interestGroup === "self" ? "➞ Eigene Testgruppen" : item.name;
          return (
            <option key={`selection-${index}`} value={item._id}>
              {label}
            </option>
          );
        });
    }
    if (prefix === "client") {
      items = array.sort(sortBy("name")).map((item, index) => {
        let disabled = item.hasOwnProperty("isActivated") && !item.isActivated ? true : null;
        return (
          <option key={`selection-${index}`} value={item._id} disabled={disabled}>
            {item.name}
          </option>
        );
      })
    }
    return (
      <Field as="select" name={fieldName}>
        <option>{defVal}</option>
        {items}
      </Field>
    );
  };

  const onSubmit = (values, { resetForm, setSubmitting }) => {
    let payload = { ...values };

    let confirm = window.confirm(`Wollen Sie wirklich ${getSelectionName(payload[formValues[prefix].selectionType])} ${payload.licencecount} Lizenzen zuweisen?`);
    if (confirm) {
      DataFetcher.fetch(targetURL, payload)
        .then(response => {
          if (!response.success) {
            setSubmitting(false);
            return appDispatch(dispError(response.msg));
          }
          appDispatch(dispSuccess(`${getAssignedLicences(response.client.log.pop().data)} Lizenzen zugewiesen`));
          if(prefix === "institution") {
            let sail = Boolean(response.institution.interestGroup === 'self') ? response.institution.licences : null;
            appDispatch({ type: "update-client-licences", data: response.client.licences, sail });
          }
          resetForm({ values: "" });
          loadData();
        })
        .catch(error => {
          console.error(error);
          setSubmitting(false);
        });
    } else {
      setSubmitting(false);
    }
  };

  return (
    <>
      {!selection && <LoadingIndicator />}
      {selection && (
        <Formik initialValues={defaultValues} validationSchema={Yup.object(yupObject)} onSubmit={onSubmit}>
          {({ errors, touched, isSubmitting }) => (
            <Form autoComplete="off">
              <div className="field">
                <label className="label">{formValues[prefix].selectionLabel}*</label>
                <div className="control">
                  <div className={`select${touched[formValues[prefix].selectionType] && errors[formValues[prefix].selectionType] ? " is-danger" : ""}`}>
                    {getDropdownOptions(selection, formValues[prefix].selectionType)}
                  </div>
                </div>
                <ErrorMessage component={ErrorLine} name={formValues[prefix].selectionType} />
              </div>
              <div className="field">
                <label className="label">Anzahl Lizenzen* {maxLicences ? `(noch ${maxLicences} verfügbare Lizenzen)` : ""}</label>
                <div className="control">
                  <Field name="licencecount" type="number" max={maxLicences} className={`input${touched.licencecount && errors.licencecount ? " is-danger" : ""}`} />
                </div>
                <ErrorMessage component={ErrorLine} name="licencecount" />
              </div>
              {prefix !== "client" && (
                <div className="field">
                  <label className="label">Gültig bis*</label>
                  <div className="control">
                    <Field name="valid_until" type="date" className={`input${touched.valid_until && errors.valid_until ? " is-danger" : ""}`} />
                  </div>
                  <ErrorMessage component={ErrorLine} name="valid_until" />
                </div>
              )}
              <div className="field">
                <div className="control field-body">
                  <button className="button is-success" type="submit" disabled={isSubmitting}>
                    Versenden
                  </button>
                  <button className="button ml-2" type="button" onClick={props.onCancel}>
                    Abbrechen
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
}
export default FormLicencesAssign;
